import { useEffect, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { loadBulkProcessQueue } from '@/lib/bulkProcessQueuePersistence';
import { useAdaptiveBulkQueuePoll } from '@/lib/bulkQueuePollScheduler';
import { createApplicationListSync } from '@/lib/applicationListSync';
import { syncBulkQueueProgress } from '@/lib/syncBulkQueueProgress';
import { useCompanyApplicationsListCache } from '@/hooks/useCompanyApplicationsListCache';
import { useBulkProcessBannerStore } from '@/stores/bulkProcessBannerStore';

/**
 * Keeps PostUpgradeBanner "processed N of K" in sync while bulk screening runs,
 * even when the user leaves the Applications page (queue persisted in sessionStorage).
 */
export function useBulkProcessBannerSync(companyId: string | undefined): void {
  const queryClient = useQueryClient();
  const { applications, hasCache } = useCompanyApplicationsListCache(companyId, false);
  const bulkPhase = useBulkProcessBannerStore((s) => s.phase);
  const startBulkRun = useBulkProcessBannerStore((s) => s.startBulkRun);
  const syncProgress = useBulkProcessBannerStore((s) => s.syncProgress);
  const sessionBatch = useMemo(
    () => (companyId ? loadBulkProcessQueue(companyId).targetIds.length > 0 : false),
    [companyId, bulkPhase],
  );

  // Resume banner after navigation — sync first; never poll until banner phase is active.
  useEffect(() => {
    if (!companyId || bulkPhase === 'active') return;
    const loaded = loadBulkProcessQueue(companyId);
    if (loaded.targetIds.length === 0) return;

    let cancelled = false;

    void (async () => {
      await syncBulkQueueProgress(queryClient, companyId, syncProgress, {
        targetIds: loaded.targetIds,
        forceListRefetch: !hasCache,
        resumeHydrate: true,
      });
      if (cancelled) return;

      const loadedAfter = loadBulkProcessQueue(companyId);
      if (loadedAfter.targetIds.length === 0) return;

      const banner = useBulkProcessBannerStore.getState();
      if (banner.phase === 'active') return;

      const listSync = createApplicationListSync(queryClient);
      const apps = listSync.getFlattenedListApplications(companyId, false);

      startBulkRun(companyId, loadedAfter.targetIds, {
        resume: true,
        initialCompleted: loadedAfter.completed,
      });
      syncProgress(
        companyId,
        loadedAfter.processingIds,
        loadedAfter.stoppingIds,
        apps,
      );
    })();

    return () => {
      cancelled = true;
    };
  }, [companyId, bulkPhase, queryClient, startBulkRun, syncProgress, hasCache]);

  // Reconcile banner when list cache updates (infinite fetch, patch, poll).
  useEffect(() => {
    if (!companyId || bulkPhase !== 'active' || applications.length === 0) return;

    const banner = useBulkProcessBannerStore.getState();
    if (banner.companyId !== companyId || banner.targetIds.length === 0) return;

    const loaded = loadBulkProcessQueue(companyId);
    syncProgress(companyId, loaded.processingIds, loaded.stoppingIds, applications);
  }, [companyId, bulkPhase, applications, syncProgress]);

  const active = !!companyId && bulkPhase === 'active';
  useAdaptiveBulkQueuePoll(active, companyId, queryClient, syncProgress);
}
